import { coverMediaKey, displayAddress, withMovedOut, type V2Inspection } from '../domain/inspection';
import { contextKindOf } from '../domain/inspectionContext';
import { reportMediaKey } from '../domain/report';
import { checkHolder, type InspectionHolder } from '../../supabase/functions/_shared/billingPolicy';
import { openPackageSink, type PackageSink } from '../native/packageFile';
import { activeAccountId } from '../persistence/accountScope';
import { normalizeSnapshot, type RawSnapshot } from '../persistence/schema';
import { deleteMediaEntries, getMediaEntry, putMediaEntries, putRecords, thumbnailKeyOf, type MediaBytes, type RecordWrite } from '../persistence/v2Database';
import { blobSource, readZipDirectory, readZipEntry, ZipFormatError, ZipWriter, type ByteSource, type ZipEntry } from '../transfer/zip';
import { deviceId, thisDevice } from './accountStore';
import { aiJobsStore } from './aiJobsStore';
import { aiStore } from './aiStore';
import { areaFocusStore, rememberCurrentArea } from './areaFocus';
import { holdImportedInspection, refreshBilling, serverRecordOf } from './billingStore';
import { deleteInspectionAction } from './deleteInspection';
import { evidenceStore } from './evidenceStore';
import { findingStore } from './findingStore';
import { reloadAccountData } from './hydrate';
import { inspectionStore, upsertInspectionLocal } from './inspectionStore';
import { poolAiLiveOf, poolAiStore } from './poolAiStore';
import { adoptRemoteSignLink, remoteSignLinkFor } from './remoteSign';
import { reportStore } from './reportStore';

// CARRYING ONE UNFINISHED INSPECTION TO ANOTHER DEVICE (Owner 2026-10-20: 「把一单导出成文件搬到另一台设备」).
// An unfinished inspection lives on ONE device and is never synced through the cloud (Owner decision list
// 2026-10-19 item 45, model A). To finish it elsewhere — photos and the agreement on the phone at the house,
// the report on the computer at the office — the inspector exports it to ONE file and imports that file on
// the other device, carrying it there by hand (AirDrop, Files, a cable, a drive). Our servers never see it.
//
//   export  every record of the inspection and every byte of its photos, cover and PDFs go into a ZIP
//           (src/transfer/zip.ts) written as a stream; once the system took the file, THIS copy becomes
//           read-only ("moved out") so the two devices can never both change it.
//   import  the file is checked (format, account, one inspection, every record through the same schema
//           validation as sign-in, every byte's CRC), the bytes are written first and the records last in
//           one transaction, the stores are reloaded, and the server is told which device holds it now
//           (billingPolicy.ts V-3 — same inspection id, same coverage: moving never charges again).
//   undo    a moved-out copy may become workable again while no other device has taken it.
// This is a V2 file of THIS product only: a record that does not validate refuses the whole file. It is not,
// and never becomes, an import of the legacy app's data (persistenceContract.ts).
export const MOVE_FORMAT = 'yanwushi-inspection-move';
export const MOVE_FORMAT_VERSION = 1;
const MANIFEST = 'inspection.json';
const README = 'README.txt';

interface MediaRef {
  key: string;
  type: string;
  file: string;
  size: number;
}

export interface MoveManifest {
  format: typeof MOVE_FORMAT;
  formatVersion: number;
  exportedAt: number;
  accountId: string;
  inspectionId: string;
  address: string;
  from: InspectionHolder;
  records: RawSnapshot;
  media: MediaRef[];
  extras: { remoteSign: unknown; currentArea: { areaId: string; at: number } | null };
}

const README_TEXT = [
  'AI 验屋师 · 一单验屋（搬到另一台设备用）',
  '',
  '这个文件是在验屋师 App 里用「搬到另一台设备」导出的一单没做完的验屋：照片、检查记录、协议都在里面。',
  '在另一台设备上打开验屋师，登录同一个账号，进「验屋」，点「从文件导入一单」，选这个文件。',
  '文件里有客户资料和照片，导入完成后请把这个文件删掉。请不要解压后再重新压缩，那样就导入不了了。',
  '',
  'AI Home Inspector · one inspection, packed to move to another device',
  '',
  'This file was made with “Move to another device” in the AI Home Inspector app: the photos, the checklist and the agreement of one unfinished inspection.',
  'On the other device, open the app, sign in to the same account, go to Inspections and tap “Import an inspection from a file”.',
  'It holds your client’s details and photos: delete the file once it has been imported. Do not unzip and re-zip it; that file cannot be imported.'
].join('\r\n');

// ---------- export ----------

export type ExportBlock = 'notFound' | 'finalized' | 'movedOut' | 'aiRunning' | 'notSignedIn';

export type ExportResult =
  | { kind: 'exported'; fileName: string; code: 'shared' | 'downloaded'; bytes: number }
  | { kind: 'cancelled' }
  | { kind: 'blocked'; reason: ExportBlock }
  | { kind: 'failed'; detail: string };

export interface ExportDeps {
  sink?: (fileName: string) => PackageSink;
  now?: number;
  // The start of the file name, in the inspector's language (「验屋搬家」 / "Inspection move").
  prefix?: string;
}

function inspectionOf(inspectionId: string): V2Inspection | null {
  return inspectionStore.get().inspections.find((i) => i.id === inspectionId) ?? null;
}

// Why this inspection cannot be exported right now, or null. AI running here would write into a copy that
// is about to become read-only, so the inspector waits for it first.
export function exportBlockFor(inspectionId: string): ExportBlock | null {
  const inspection = inspectionOf(inspectionId);
  if (!inspection) return 'notFound';
  if (inspection.finalizedAt !== null) return 'finalized';
  if (inspection.movedOut !== null) return 'movedOut';
  const areaIds = new Set(inspectionStore.get().areas.filter((a) => a.inspectionId === inspectionId).map((a) => a.id));
  const ai = aiStore.get();
  if (poolAiLiveOf(poolAiStore.get(), inspectionId) !== null || Object.keys(ai.live).some((id) => areaIds.has(id)) || Object.entries(ai.wordingLive).some(([id, busy]) => busy && areaIds.has(id))) return 'aiRunning';
  if (!activeAccountId()) return 'notSignedIn';
  return null;
}

// About how large the file will be (photos + cover + PDFs as recorded), for the confirmation.
export function estimateExportBytes(inspectionId: string): number {
  const inspection = inspectionOf(inspectionId);
  const photos = evidenceStore.get().evidence.filter((e) => e.inspectionId === inspectionId).reduce((total, e) => total + Math.max(0, e.byteSize), 0);
  const pdfs = reportStore.get().reports.filter((r) => r.inspectionId === inspectionId).reduce((total, r) => total + Math.max(0, r.byteSize), 0);
  return photos + pdfs + (inspection?.coverPhoto?.byteSize ?? 0);
}

function safeFilePart(text: string): string {
  return text
    .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60);
}

export function moveFileName(inspection: V2Inspection, now: number, prefix = 'inspection-move'): string {
  const day = new Date(now);
  const stamp = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
  const street = safeFilePart(inspection.property.address);
  return `${[safeFilePart(prefix) || 'inspection-move', street, stamp].filter((part) => part.length > 0).join('-')}.zip`;
}

function extensionFor(type: string): string {
  if (type === 'image/jpeg') return '.jpg';
  if (type === 'image/png') return '.png';
  if (type === 'image/webp') return '.webp';
  if (type === 'image/heic' || type === 'image/heif') return '.heic';
  if (type === 'application/pdf') return '.pdf';
  return '.bin';
}

function recordsOf(inspection: V2Inspection): RawSnapshot {
  const id = inspection.id;
  const state = inspectionStore.get();
  const ai = aiStore.get();
  return {
    inspections: [inspection],
    areas: state.areas.filter((a) => a.inspectionId === id),
    items: state.items.filter((i) => i.inspectionId === id),
    evidence: evidenceStore.get().evidence.filter((e) => e.inspectionId === id),
    findings: findingStore.get().findings.filter((f) => f.inspectionId === id),
    reports: reportStore.get().reports.filter((r) => r.inspectionId === id),
    aiRuns: ai.runs.filter((r) => r.inspectionId === id),
    aiObservations: ai.observations.filter((o) => o.inspectionId === id),
    aiJobs: aiJobsStore.get().jobs.filter((j) => j.inspectionId === id)
  };
}

// Every media key that belongs to this inspection: its photos, its cover, its PDFs, and their thumbnails.
function mediaKeysOf(records: RawSnapshot, inspection: V2Inspection): string[] {
  const keys: string[] = [];
  for (const evidence of records.evidence as { id: string }[]) keys.push(evidence.id, thumbnailKeyOf(evidence.id));
  if (inspection.coverPhoto) keys.push(coverMediaKey(inspection.id), thumbnailKeyOf(coverMediaKey(inspection.id)));
  for (const report of (records.reports ?? []) as { id: string }[]) keys.push(reportMediaKey(report.id));
  return keys;
}

const encoder = new TextEncoder();

export async function exportInspectionAction(inspectionId: string, deps: ExportDeps = {}): Promise<ExportResult> {
  const block = exportBlockFor(inspectionId);
  if (block) return { kind: 'blocked', reason: block };
  const inspection = inspectionOf(inspectionId);
  const accountId = activeAccountId();
  if (!inspection || !accountId) return { kind: 'blocked', reason: 'notSignedIn' };
  const now = deps.now ?? Date.now();
  const stamp = new Date(now);
  const records = recordsOf(inspection);
  const fileName = moveFileName(inspection, now, deps.prefix);
  const sink = (deps.sink ?? openPackageSink)(fileName);
  const writer = new ZipWriter((chunk) => sink.write(chunk));
  const media: MediaRef[] = [];
  try {
    for (const key of mediaKeysOf(records, inspection)) {
      const entry = await getMediaEntry(key);
      if (!entry) continue;
      const file = `media/${String(media.length + 1).padStart(4, '0')}${extensionFor(entry.type)}`;
      await writer.add(file, new Uint8Array(entry.bytes), stamp);
      media.push({ key, type: entry.type, file, size: entry.bytes.byteLength });
    }
    const focus = areaFocusStore.get().byInspection[inspectionId] ?? null;
    const manifest: MoveManifest = {
      format: MOVE_FORMAT,
      formatVersion: MOVE_FORMAT_VERSION,
      exportedAt: now,
      accountId,
      inspectionId,
      address: displayAddress(inspection, ''),
      from: thisDevice(),
      records,
      media,
      extras: { remoteSign: remoteSignLinkFor(inspectionId), currentArea: focus }
    };
    await writer.add(MANIFEST, encoder.encode(JSON.stringify(manifest)), stamp);
    await writer.add(README, encoder.encode(README_TEXT), stamp);
    await writer.finish();
  } catch (raw) {
    await sink.abort().catch(() => undefined);
    return { kind: 'failed', detail: raw instanceof Error ? raw.message : String(raw) };
  }
  const outcome = await sink.finish();
  if (outcome.code === 'cancelled') return { kind: 'cancelled' };
  if (outcome.code === 'failed') return { kind: 'failed', detail: outcome.detail };
  // The system has the file: from now on the work goes on wherever it is imported.
  const current = inspectionOf(inspectionId);
  if (current && current.movedOut === null) {
    const next = withMovedOut(current, { at: now, fileName }, now);
    await putRecords([{ store: 'inspections', value: next }]);
    upsertInspectionLocal(next);
  }
  return { kind: 'exported', fileName, code: outcome.code, bytes: writer.bytesWritten };
}

// ---------- the moved-out copy ----------

// Where the server says a moved-out copy went (V-2/V-3), or null while no other device has taken it.
export function movedToOf(inspectionId: string): InspectionHolder | null {
  const record = serverRecordOf(inspectionId);
  return record?.holder && record.holder.deviceId !== deviceId() ? record.holder : null;
}

export type UndoMoveResult = { kind: 'undone' } | { kind: 'refused'; reason: 'notMoved' | 'heldElsewhere' };

// Work on the moved-out copy again — only while no other device has imported it.
export async function undoMoveOutAction(inspectionId: string, now: number = Date.now()): Promise<UndoMoveResult> {
  await refreshBilling().catch(() => undefined);
  const current = inspectionOf(inspectionId);
  if (!current || current.movedOut === null) return { kind: 'refused', reason: 'notMoved' };
  if (movedToOf(inspectionId)) return { kind: 'refused', reason: 'heldElsewhere' };
  const next = withMovedOut(current, null, now);
  await putRecords([{ store: 'inspections', value: next }]);
  upsertInspectionLocal(next);
  return { kind: 'undone' };
}

// ---------- import ----------

export type ImportProblem = 'notAFile' | 'damaged' | 'newerFormat' | 'otherAccount' | 'alreadyHere' | 'finished' | 'notSignedIn' | 'storageFull' | 'failed';

export type ImportResult =
  | { kind: 'imported'; inspectionId: string; address: string; replaced: boolean; photos: number }
  | { kind: 'refused'; reason: ImportProblem; detail: string };

type Rec = Record<string, unknown>;

function isRec(value: unknown): value is Rec {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function refuse(reason: ImportProblem, detail = ''): ImportResult {
  return { kind: 'refused', reason, detail };
}

function readManifest(raw: unknown): { ok: true; manifest: MoveManifest } | { ok: false; reason: ImportProblem } {
  if (!isRec(raw) || raw.format !== MOVE_FORMAT) return { ok: false, reason: 'notAFile' };
  if (typeof raw.formatVersion !== 'number' || raw.formatVersion > MOVE_FORMAT_VERSION) return { ok: false, reason: 'newerFormat' };
  const records = isRec(raw.records) ? raw.records : null;
  const from = checkHolder(raw.from);
  if (typeof raw.accountId !== 'string' || typeof raw.inspectionId !== 'string' || !records || !from || !Array.isArray(raw.media)) return { ok: false, reason: 'damaged' };
  const list = (key: string): unknown[] => (Array.isArray(records[key]) ? (records[key] as unknown[]) : []);
  const media: MediaRef[] = [];
  for (const entry of raw.media) {
    if (!isRec(entry) || typeof entry.key !== 'string' || typeof entry.type !== 'string' || typeof entry.file !== 'string' || typeof entry.size !== 'number') return { ok: false, reason: 'damaged' };
    media.push({ key: entry.key, type: entry.type, file: entry.file, size: entry.size });
  }
  const extras = isRec(raw.extras) ? raw.extras : {};
  const focus = isRec(extras.currentArea) && typeof extras.currentArea.areaId === 'string' ? { areaId: extras.currentArea.areaId, at: typeof extras.currentArea.at === 'number' ? extras.currentArea.at : 0 } : null;
  return {
    ok: true,
    manifest: {
      format: MOVE_FORMAT,
      formatVersion: raw.formatVersion,
      exportedAt: typeof raw.exportedAt === 'number' ? raw.exportedAt : 0,
      accountId: raw.accountId,
      inspectionId: raw.inspectionId,
      address: typeof raw.address === 'string' ? raw.address : '',
      from,
      records: {
        inspections: list('inspections'),
        areas: list('areas'),
        items: list('items'),
        evidence: list('evidence'),
        findings: list('findings'),
        reports: list('reports'),
        aiRuns: list('aiRuns'),
        aiObservations: list('aiObservations'),
        aiJobs: list('aiJobs')
      },
      media,
      extras: { remoteSign: extras.remoteSign ?? null, currentArea: focus }
    }
  };
}

function isStorageFull(raw: unknown): boolean {
  return raw instanceof Error && (raw.name === 'QuotaExceededError' || /quota/i.test(raw.message));
}

// Media are written a few megabytes at a time; the records that point at them go last.
const MEDIA_BATCH_BYTES = 8 * 1024 * 1024;

export async function importInspectionFile(file: Blob | ByteSource): Promise<ImportResult> {
  const accountId = activeAccountId();
  if (!accountId) return refuse('notSignedIn');
  const source: ByteSource = file instanceof Blob ? blobSource(file) : file;

  let entries: ZipEntry[];
  try {
    entries = await readZipDirectory(source);
  } catch (raw) {
    return refuse(raw instanceof ZipFormatError && /cut short|damaged|repacked/.test(raw.message) ? 'damaged' : 'notAFile', raw instanceof Error ? raw.message : '');
  }
  const byName = new Map(entries.map((entry) => [entry.name, entry] as const));
  const manifestEntry = byName.get(MANIFEST);
  if (!manifestEntry) return refuse('notAFile');
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(await readZipEntry(source, manifestEntry))) as unknown;
  } catch (raw) {
    return refuse('damaged', raw instanceof Error ? raw.message : '');
  }
  const read = readManifest(parsed);
  if (!read.ok) return refuse(read.reason);
  const manifest = read.manifest;
  if (manifest.accountId !== accountId) return refuse('otherAccount');

  // The records, through the same validation as every sign-in. Anything that does not validate refuses the file.
  const snapshot = normalizeSnapshot(manifest.records);
  const incoming = snapshot.inspections[0];
  if (snapshot.dropped > 0 || snapshot.inspections.length !== 1 || !incoming || incoming.id !== manifest.inspectionId) return refuse('damaged');
  const inspectionId = incoming.id;
  const children = [...snapshot.areas, ...snapshot.items, ...snapshot.evidence, ...snapshot.findings, ...snapshot.reports, ...snapshot.aiRuns, ...snapshot.aiObservations, ...snapshot.aiJobs];
  if (children.some((child) => child.inspectionId !== inspectionId)) return refuse('damaged');
  if (incoming.finalizedAt !== null) return refuse('finished');

  const local = inspectionOf(inspectionId);
  if (local && local.finalizedAt !== null) return refuse('finished');
  // The same inspection, still workable here: the work on this device is newer than any file of it.
  if (local && local.movedOut === null) return refuse('alreadyHere');

  // Only this inspection's own bytes, each present in the archive.
  const allowed = new Set(mediaKeysOf(snapshot, incoming));
  if (incoming.coverPhoto === null) {
    allowed.delete(coverMediaKey(inspectionId));
    allowed.delete(thumbnailKeyOf(coverMediaKey(inspectionId)));
  }
  for (const ref of manifest.media) {
    const entry = byName.get(ref.file);
    if (!allowed.has(ref.key) || !entry || entry.size !== ref.size) return refuse('damaged');
  }

  // A copy that went out from here comes back: it is replaced by the file. (It gives nothing back — it was
  // moved out — and its server records are the file's too.)
  if (local) await deleteInspectionAction(inspectionId);

  const written: string[] = [];
  try {
    let batch: { key: string; value: MediaBytes }[] = [];
    let batchBytes = 0;
    for (const ref of manifest.media) {
      const bytes = await readZipEntry(source, byName.get(ref.file) as ZipEntry);
      batch.push({ key: ref.key, value: { type: ref.type, bytes: bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer } });
      batchBytes += bytes.byteLength;
      if (batchBytes >= MEDIA_BATCH_BYTES) {
        await putMediaEntries(batch);
        written.push(...batch.map((entry) => entry.key));
        batch = [];
        batchBytes = 0;
      }
    }
    await putMediaEntries(batch);
    written.push(...batch.map((entry) => entry.key));
    const writes: RecordWrite[] = [
      { store: 'inspections', value: { ...incoming, movedOut: null } },
      ...snapshot.areas.map((value) => ({ store: 'areas' as const, value })),
      ...snapshot.items.map((value) => ({ store: 'items' as const, value })),
      ...snapshot.evidence.map((value) => ({ store: 'evidence' as const, value })),
      ...snapshot.findings.map((value) => ({ store: 'findings' as const, value })),
      ...snapshot.reports.map((value) => ({ store: 'reports' as const, value })),
      ...snapshot.aiRuns.map((value) => ({ store: 'aiRuns' as const, value })),
      ...snapshot.aiObservations.map((value) => ({ store: 'aiObservations' as const, value })),
      ...snapshot.aiJobs.map((value) => ({ store: 'aiJobs' as const, value }))
    ];
    await putRecords(writes);
  } catch (raw) {
    await deleteMediaEntries(written).catch(() => undefined);
    if (raw instanceof ZipFormatError) return refuse('damaged', raw.message);
    return refuse(isStorageFull(raw) ? 'storageFull' : 'failed', raw instanceof Error ? raw.message : String(raw));
  }

  await reloadAccountData();
  adoptRemoteSignLink(manifest.extras.remoteSign);
  if (manifest.extras.currentArea) rememberCurrentArea(inspectionId, manifest.extras.currentArea.areaId, manifest.extras.currentArea.at);
  // V-3: this device holds it now (queued while offline). A Demo / test inspection was never recorded by the
  // server — and must never be charged for by a hold that finds nothing.
  if (contextKindOf(incoming) === 'realClient') void holdImportedInspection(inspectionId).catch(() => undefined);
  return { kind: 'imported', inspectionId, address: displayAddress(incoming, ''), replaced: local !== null, photos: snapshot.evidence.length };
}
